import * as selectors from './selectors';

import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import {
  archiveCase,
  archiveCaseError,
  archiveCaseSuccess,
  assignUser,
  assignUserClose,
  assignUserError,
  assignUserOpen,
  assignUserOpenError,
  assignUserOpenSuccess,
  assignUserSuccess,
  dearchiveCase,
  dearchiveCaseError,
  dearchiveCaseSuccess,
  fetchCaseForMobile,
  fetchCaseForMobileError,
  fetchCaseForMobileSuccess,
  fetchCaseTypeCodesError,
  fetchCaseTypeCodesSuccess,
  fetchCases,
  fetchCasesCount,
  fetchCasesCountError,
  fetchCasesCountSuccess,
  fetchCasesError,
  fetchCasesSuccess,
  markCaseAsReadError,
  markCaseAsReadSuccess,
  refuseCase,
  refuseCaseError,
  refuseCaseSuccess,
  selectCase,
  setNoCaseFoundMessage,
  fetchPartiesError,
  fetchPartiesSuccess,
  fetchChildPartiesError,
  fetchChildPartiesSuccess,
  fetchChildParties,
  fetchParties,
  fetchAssignedPartiesError,
  fetchAssignedPartiesSuccess,
  setInProgressState,
  setInProgressStateSuccess,
  setInProgressStateError,
  navigateTo
} from './actions';
import {
  archiveCase as archiveCaseApi,
  dearchiveCase as dearchiveCaseApi,
  getCaseForMobile,
  getCaseTypeCodes,
  getCases,
  getCasesCount,
  markCaseAsRead,
  refuseCase as refuseCaseApi,
  setInProgressState as setInProgressStateApi
  } from '../utils/api/casesApi';
import { getParties as getPartiesApi, getChildParties as getChildPartiesApi, getAssignedParties } from '../utils/api/partyApi';
import { assignUser as assignUserApi, getAssignableUsers } from '../utils/api/usersApi';

import { CaseActionTypes } from './types';
import { toggleIsUIDisabled } from '../shared/actions';
import { SagaIterator } from 'redux-saga';

function* handleFetchCases(action: ReturnType<typeof fetchCases>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const [cases, caseTypeCodes] = yield all([
      call(getCases, action.payload.caseSearch, action.payload.offset),
      call(getCaseTypeCodes)
    ]);

    if (!cases.data) {
      yield put(fetchCasesError(JSON.stringify(cases.response)));
    } else if (!caseTypeCodes.data) {
      yield put(fetchCaseTypeCodesError(JSON.stringify(caseTypeCodes.error)));
    } else {
      yield put(fetchCasesSuccess(cases.data, action.payload.searchClicked));
      yield put(fetchCaseTypeCodesSuccess(caseTypeCodes.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchCasesError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchCasesError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleFetchCaseForMobile(action: ReturnType<typeof fetchCaseForMobile>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const [cases, caseTypeCodes] = yield all([
      call(getCaseForMobile, action.payload.searchCase),
      call(getCaseTypeCodes)
    ]);

    if (!cases.data) {
      yield put(fetchCaseForMobileError(JSON.stringify(cases)));
    } else if (!caseTypeCodes.data) {
      yield put(fetchCaseTypeCodesError(JSON.stringify(caseTypeCodes.error)));
    } else {
      if (cases.data.length === 0) {
        yield put(setNoCaseFoundMessage());
      } else {
        yield put(fetchCaseForMobileSuccess(cases.data));
        yield put(fetchCaseTypeCodesSuccess(caseTypeCodes.data));
        yield put(navigateTo(`/images/${cases.data[0].caseCode}`));
      }
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchCaseForMobileError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchCasesError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleRefuseCase(action: ReturnType<typeof refuseCase>): SagaIterator {
  try {
    const refuseCaseResponse = yield call(refuseCaseApi, action.payload.selectedCase);

    if (refuseCaseResponse.status > 299) {
      if (refuseCaseResponse.response && refuseCaseResponse.response.data) {
        yield put(refuseCaseError(refuseCaseResponse.response.data.error));
      }
    } else {
      yield put(refuseCaseSuccess(action.payload.selectedCase));
      yield put(fetchCasesCount());
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(refuseCaseError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(refuseCaseError('An unknown error occured.'));
    }
  }
}

function* handleArchiveCase(action: ReturnType<typeof archiveCase>): SagaIterator {
  try {
    const archiveCaseResponse = yield call(archiveCaseApi, action.payload.selectedCase);

    if (archiveCaseResponse.status > 299) {
      yield put(archiveCaseError(archiveCaseResponse.error));
    } else {
      yield put(archiveCaseSuccess(action.payload.selectedCase));
      yield put(fetchCasesCount());
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(archiveCaseError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(archiveCaseError('An unknown error occured.'));
    }
  }
}

function* handleDearchiveCase(action: ReturnType<typeof dearchiveCase>): SagaIterator {
  try {
    const dearchiveCaseResponse = yield call(dearchiveCaseApi, action.payload.selectedCase);

    if (dearchiveCaseResponse.status > 299) {
      yield put(dearchiveCaseError(dearchiveCaseResponse.error));
    } else {
      yield put(dearchiveCaseSuccess(action.payload.selectedCase));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(dearchiveCaseError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(dearchiveCaseError('An unknown error occured.'));
    }
  }
}

function* handleAssignUserOpen(action: ReturnType<typeof assignUserOpen>): SagaIterator {
  try {
    const res = yield call(getAssignableUsers);

    if (res.error) {
      yield put(assignUserOpenError(res.error));
    } else {
      yield put(assignUserOpenSuccess(res.data, action.payload.claimNotificationId));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(assignUserOpenError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(assignUserOpenError('An unknown error occured.'));
    }
  }
}

function* handleAssignUser(action: ReturnType<typeof assignUser>): SagaIterator {
  try {
    const res = yield call(assignUserApi, action.payload.userId, action.payload.claimNotificationId);
    yield put(assignUserClose());
    if (res.error) {
      yield put(assignUserError(res.error));
    } else {
      yield put(assignUserSuccess(action.payload.userId, action.payload.claimNotificationId, action.payload.expertName));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(assignUserOpenError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(assignUserOpenError('An unknown error occured.'));
    }
  }
}

function* handleResetCaseState(): SagaIterator {
  const caseSearch = yield select(selectors.caseSearch);

  yield put(fetchCases(caseSearch, 0, false));
  yield put(fetchCasesCount());
}

function* handleFetchCasesCount(action: ReturnType<typeof fetchCasesCount>): SagaIterator {
  try {
    const fetchCasesCountResponse = yield call(getCasesCount);

    if (fetchCasesCountResponse.error) {
      yield put(fetchCasesCountError(fetchCasesCountResponse.error));
    } else {
      yield put(fetchCasesCountSuccess(fetchCasesCountResponse.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchCasesCountError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchCasesCountError('An unknown error occured.'));
    }
  }
}

function* handleSelectCase(action: ReturnType<typeof selectCase>): SagaIterator {
  try {
    if (action.payload.selectedCase.isRead !== true) {
      const markCaseAsReadResponse = yield call(markCaseAsRead, action.payload.selectedCase);
      
      if (markCaseAsReadResponse.status > 299) {
        yield put(markCaseAsReadError(markCaseAsReadResponse.error));
      } else {
        yield put(markCaseAsReadSuccess(action.payload.selectedCase));
      }
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(markCaseAsReadError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(markCaseAsReadError('An unknown error occured.'));
    }
  }
}

function* handleFetchParties(action: ReturnType<typeof fetchParties>): SagaIterator {
  try {
    const parties =  yield call(getPartiesApi);
    if (!parties.data) {
      yield put(fetchPartiesError(JSON.stringify(parties.response)));
    } else {
      yield put(fetchPartiesSuccess(parties.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchPartiesError((`${err.message}\n${err.name}\n${err.stack}`)));
    } else {
      yield put(fetchPartiesError('An unknown error occured.'));
    }
  }
}

function* handleFetchChildParties(action: ReturnType<typeof fetchChildParties>): SagaIterator {
  try {
    const parties =  yield call(getChildPartiesApi, action.payload.partyId);
    if (!parties.data) {
      yield put(fetchChildPartiesError(JSON.stringify(parties.response)));
    } else {
      yield put(fetchChildPartiesSuccess(parties.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchChildPartiesError((`${err.message}\n${err.name}\n${err.stack}`)));
    } else {
      yield put(fetchChildPartiesError('An unknown error occured.'));
    }
  }
}

function* handleFetchAssignedParties(action: ReturnType<typeof fetchParties>): SagaIterator {
  try {
    const parties =  yield call(getAssignedParties);
    if (!parties.data) {
      yield put(fetchAssignedPartiesError(JSON.stringify(parties.response)));
    } else {
      yield put(fetchAssignedPartiesSuccess(parties.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchAssignedPartiesError((`${err.message}\n${err.name}\n${err.stack}`)));
    } else {
      yield put(fetchAssignedPartiesError('An unknown error occured.'));
    }
  }
}

function* handleSetInProgressState(action: ReturnType<typeof setInProgressState>): SagaIterator {
  const selectedCase = yield select(selectors.selectedCase);
  try {
    const setInProgresStateCaseResponse = yield call(setInProgressStateApi, selectedCase);

    if (setInProgresStateCaseResponse.response && setInProgresStateCaseResponse.response.status === 500) {
      yield put(setInProgressStateError(setInProgresStateCaseResponse.error, selectedCase));
    } else {
      yield put(setInProgressStateSuccess(action.payload.selectedCase));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(setInProgressStateError(`${err.message}\n${err.name}\n${err.stack}`, selectedCase));
    } else {
      yield put(setInProgressStateError('An unknown error occured.', selectedCase));
    }
  }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchCasesRequests() {
  yield takeEvery(CaseActionTypes.FETCH_CASE_FOR_MOBILE, handleFetchCaseForMobile);
  yield takeEvery(CaseActionTypes.FETCH_CASES, handleFetchCases);
  yield takeEvery(CaseActionTypes.REFUSE_CASE, handleRefuseCase);
  yield takeEvery(CaseActionTypes.ARCHIVE_CASE, handleArchiveCase);
  yield takeEvery(CaseActionTypes.DEARCHIVE_CASE, handleDearchiveCase);
  yield takeEvery(CaseActionTypes.ASSIGN_USER_OPEN, handleAssignUserOpen);
  yield takeEvery(CaseActionTypes.ASSIGN_USER, handleAssignUser);
  yield takeEvery(CaseActionTypes.RESET_STATE, handleResetCaseState);
  yield takeEvery(CaseActionTypes.FETCH_CASES_COUNT, handleFetchCasesCount);
  yield takeEvery(CaseActionTypes.SELECT_CASE, handleSelectCase);
  yield takeEvery(CaseActionTypes.FETCH_PARTIES, handleFetchParties);
  yield takeEvery(CaseActionTypes.FETCH_CHILD_PARTIES, handleFetchChildParties);
  yield takeEvery(CaseActionTypes.FETCH_ASSIGNED_PARTIES, handleFetchAssignedParties);
  yield takeEvery(CaseActionTypes.SET_IN_PROGRESS_STATE, handleSetInProgressState);
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* caseSaga() {
  yield all([fork(watchCasesRequests)]);
}

export default caseSaga;
